import {
  WeightUnit,
  CurrencyCode,
  Product,
} from '@shopify/hydrogen/storefront-api-types';
import {RebuyProductEntity} from '~/types/RebuyProductsEntity';

export const weightUnitMap: Record<string, WeightUnit> = {
  lb: WeightUnit.Ounces,
  oz: WeightUnit.Ounces,
  g: WeightUnit.Grams,
  kg: WeightUnit.Kilograms,
  pounds: WeightUnit.Pounds,
};

export function normalizeRebuyProducts(
  products: RebuyProductEntity[],
): Product[] {
  return products.map((product) => ({
    // TODO: Improve the out of stock logic
    availableForSale: product.variants.some(
      (variant) => variant.inventory_quantity > 0,
    ),
    collections: {
      edges: [],
      nodes: [],
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
    },
    compareAtPriceRange: {
      minVariantPrice: {
        amount: Math.min(
          ...product.variants.map((variant) =>
            Number(variant.compare_at_price),
          ),
        ).toString(),
        currencyCode: CurrencyCode.Usd,
      },
      maxVariantPrice: {
        amount: Math.max(
          ...product.variants.map((variant) =>
            Number(variant.compare_at_price),
          ),
        ).toString(),
        currencyCode: CurrencyCode.Usd,
      },
    },
    createdAt: product.created_at,
    description: product.description,
    descriptionHtml: product.description,
    featuredImage: product.image && {
      src: product.image.src,
      url: product.image.src,
      originalSrc: product.image.src,
      transformedSrc: product.image.src,
    },
    handle: product.handle,
    id: product.id,
    images: {
      edges: [],
      nodes:
        product.images?.map((image) => ({
          src: image.src,
          url: image.src,
          originalSrc: image.src,
          transformedSrc: image.src,
        })) ?? [],
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
    },
    isGiftCard: false,
    media: {
      edges: [],
      nodes: [],
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
    },
    metafields: product.metafields,
    options: [],
    priceRange: {
      minVariantPrice: {
        amount: Math.min(
          ...product.variants.map((variant) => Number(variant.price)),
        ).toString(),
        currencyCode: CurrencyCode.Usd,
      },
      maxVariantPrice: {
        amount: Math.max(
          ...product.variants.map((variant) => Number(variant.price)),
        ).toString(),
        currencyCode: CurrencyCode.Usd,
      },
    },
    productType: product.product_type,
    publishedAt: product.published_at,
    requiresSellingPlan: false,
    sellingPlanGroups: {
      edges: [],
      nodes: [],
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
    },
    seo: {
      title: product.title,
      description: product.description,
    },
    tags: product.tags.split(',').map((tag) => tag.trim()),
    title: product.title,
    updatedAt: product.updated_at,
    vendor: product.vendor,
    variants: {
      edges: [],
      nodes: product.variants.map((variant) => {
        const image = product.images?.find(
          (image) => image.id === variant.image_id,
        );

        return {
          availableForSale: variant.inventory_quantity > 0,
          barcode: variant.barcode,
          compareAtPrice: {
            amount: variant.compare_at_price,
            currencyCode: CurrencyCode.Usd,
          },
          compareAtPriceV2: {
            amount: variant.compare_at_price,
            currencyCode: CurrencyCode.Usd,
          },
          // TODO: Improve the out of stock logic
          currentlyNotInStock: variant.inventory_quantity <= 0,
          id: variant.id,
          image: image
            ? {
                src: image.src,
                url: image.src,
                originalSrc: image.src,
                transformedSrc: image.src,
              }
            : undefined,
          metafields: [],
          price: {
            amount: variant.price,
            currencyCode: CurrencyCode.Usd,
          },
          priceV2: {
            amount: variant.price,
            currencyCode: CurrencyCode.Usd,
          },
          product: {} as unknown as Product,
          requiresShipping: true,
          selectedOptions: product.options.map((option) => {
            const values = option.values.map((value) => value.toLowerCase());
            const titleParts = variant.title.split(' / ');
            const optionIndex = titleParts.findIndex((part) =>
              values.includes(part.toLowerCase()),
            );

            return {
              name: option.name,
              value: optionIndex >= 0 ? titleParts[optionIndex] : '',
            };
          }),
          sellingPlanAllocations: {
            edges: [],
            nodes: [],
            pageInfo: {
              hasNextPage: false,
              hasPreviousPage: false,
            },
          },
          sku: variant.sku,
          storeAvailability: {
            edges: [],
            nodes: [],
            pageInfo: {
              hasNextPage: false,
              hasPreviousPage: false,
            },
          },
          title: variant.title,
          weight: variant.weight,
          weightUnit: weightUnitMap[variant.weight_unit] ?? WeightUnit.Pounds,
        };
      }),
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
    },
  }));
}
